import { Component, Inject, OnInit, inject, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AdaptersHttpService,
  CreateAdapterRequest,
  SplunkAdapterConfig,
  SentinelAdapterConfig,
  AdapterConfig,
  AdapterResponse,
} from 'src/app/services/httpServices/adaptersHttpService/adapters-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { SplunkAdapterFormComponent } from './splunk-adapter-form/splunk-adapter-form.component';
import { SentinelAdapterFormComponent } from './sentinel-adapter-form/sentinel-adapter-form.component';

export interface AdapterDialogData {
  isEditMode?: boolean;
  adapterId?: string;
}

export enum AdapterPlatform {
  Splunk = 'splunk',
  MicrosoftSentinel = 'sentinel',
}

interface ValidationError {
  loc: string[];
  msg: string;
  type: string;
}

interface BackendErrorResponse {
  error?: {
    detail?: string | ValidationError[];
  };
}

@Component({
  selector: 'app-adapter-dialog',
  templateUrl: './adapter-dialog.component.html',
  styleUrls: ['./adapter-dialog.component.scss'],
})
export class AdapterDialogComponent implements OnInit, AfterViewInit {
  @ViewChild(SplunkAdapterFormComponent) splunkForm?: SplunkAdapterFormComponent;
  @ViewChild(SentinelAdapterFormComponent) sentinelForm?: SentinelAdapterFormComponent;

  public adapterForm: FormGroup;
  public selectedPlatform: AdapterPlatform | null = null;
  public AdapterPlatform = AdapterPlatform;
  public isEditMode = false;
  public isLoading = false;
  public errorMessage: string | null = null;

  private _adapterDataToLoad: AdapterResponse | null = null;

  public platforms = [
    { id: AdapterPlatform.Splunk, name: 'Splunk', icon: 'assets/images/platforms/splunk.svg', disabled: false },
    {
      id: AdapterPlatform.MicrosoftSentinel,
      name: 'Microsoft Sentinel',
      icon: 'assets/images/platforms/microsoft-sentinel.svg',
      disabled: false,
    },
  ];

  private readonly _adaptersHttpService = inject(AdaptersHttpService);
  private readonly _logger = inject(LoggerService);
  private readonly _cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _dialogRef: MatDialogRef<AdapterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AdapterDialogData
  ) {
    this.isEditMode = data?.isEditMode || false;
    // Only create common fields - platform-specific fields are in child components
    this.adapterForm = this._fb.group({
      name: ['', Validators.required],
      description: [''],
      platform: ['', Validators.required],
    });
  }

  ngAfterViewInit(): void {
    // If we have adapter data to load and the child components are initialized, load the data
    if (this._adapterDataToLoad) {
      const adapterData = this._adapterDataToLoad;
      setTimeout(() => {
        this._initializeChildFormData(adapterData);
      });
    }
  }

  ngOnInit(): void {
    if (this.data?.adapterId) {
      this._loadAdapterData(this.data.adapterId);
    }
  }

  private _loadAdapterData(adapterId: string): void {
    this.isLoading = true;
    this._adaptersHttpService.getAdapterById$(adapterId).subscribe({
      next: (adapter) => {
        // First, set the platform
        this.selectedPlatform = adapter.config.adapterType as AdapterPlatform;
        this.selectPlatform(this.selectedPlatform);

        // Patch the common form values
        this.adapterForm.patchValue({
          name: adapter.name,
          description: adapter.description,
          platform: adapter.config.adapterType,
        });

        // Store adapter data to initialize child components
        this._adapterDataToLoad = adapter;

        // Trigger change detection to ensure child component is created
        this._cdr.detectChanges();

        // Now initialize child form (component should exist now)
        this._initializeChildFormData(adapter);

        this.isLoading = false;
      },
      error: (error) => {
        this._logger.error('Failed to load adapter:', error);
        this.errorMessage = 'Failed to load adapter data';
        this.isLoading = false;
      },
    });
  }

  private _initializeChildFormData(adapter: AdapterResponse): void {
    if (adapter.config.adapterType === 'splunk' && this.splunkForm) {
      const config = adapter.config as SplunkAdapterConfig;
      this.splunkForm.initializeForm(config);
    } else if (adapter.config.adapterType === 'sentinel' && this.sentinelForm) {
      const config = adapter.config as SentinelAdapterConfig;
      this.sentinelForm.initializeForm(config);
    }
  }

  public selectPlatform(platform: AdapterPlatform): void {
    this.selectedPlatform = platform;
    this.adapterForm.patchValue({ platform });
    // Child components handle their own form fields
  }

  /**
   * Get the logo path for a given platform
   * @param platformId - The platform identifier
   * @returns The path to the platform's logo image
   */
  public getPlatformLogo(platformId: AdapterPlatform): string {
    const logoMap: Record<AdapterPlatform, string> = {
      [AdapterPlatform.Splunk]: 'assets/images/logo/splunk-logo.svg',
      [AdapterPlatform.MicrosoftSentinel]: 'assets/images/logo/microsoft-sentinel-logo.svg',
    };
    return logoMap[platformId] || '';
  }

  public onCancel(): void {
    this._dialogRef.close(false);
  }

  /**
   * Parse backend validation errors and extract user-friendly messages
   * @param error - The error response from the backend
   * @returns A user-friendly error message
   */
  private _parseBackendError(error: BackendErrorResponse): string {
    const detail = error.error?.detail;

    // Handle array of validation errors
    if (Array.isArray(detail)) {
      const validationErrors = detail as ValidationError[];
      const errorMessages = validationErrors.map((err) => {
        const fieldPath = err.loc || [];
        // Get the last field name, which is usually the most specific
        const fieldName = fieldPath[fieldPath.length - 1] || 'field';
        const message = err.msg || 'Invalid value';

        // Special handling for common field names
        const fieldNameMap: Record<string, string> = {
          hecUrl: 'HEC URL',
          hecToken: 'HEC Token',
          tenantId: 'Tenant ID',
          clientId: 'Client ID',
          clientSecret: 'Client Secret',
          dceUri: 'DCE URI',
          dcrImmutableId: 'DCR Immutable ID',
          streamName: 'Stream Name',
        };

        const readableFieldName = fieldNameMap[fieldName] || this._convertToReadable(fieldName);
        return `${readableFieldName}: ${message}`;
      });

      return errorMessages.join('; ');
    }

    // Handle string detail that might contain validation error JSON
    if (typeof detail === 'string') {
      // Try to parse if it looks like JSON
      if (detail.includes('[{') && detail.includes("'loc':")) {
        try {
          // Extract the validation errors from the string
          const match = detail.match(/\[\{.*\}\]/);
          if (match) {
            // Convert Python-style dict to JSON
            const jsonStr = match[0].replace(/'/g, '"').replace(/True/g, 'true').replace(/False/g, 'false');
            const parsed = JSON.parse(jsonStr);
            if (Array.isArray(parsed)) {
              return this._parseBackendError({ error: { detail: parsed } });
            }
          }
        } catch {
          // If parsing fails, fall through to return the string as-is
        }
      }
      return detail;
    }

    // Fallback to generic message
    return `Failed to ${this.isEditMode ? 'update' : 'create'} adapter`;
  }

  /**
   * Convert camelCase or snake_case to readable format
   */
  private _convertToReadable(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .toLowerCase()
      .trim()
      .replace(/^./, (str: string) => str.toUpperCase());
  }

  public onCreate(): void {
    if (!this.isFormValid) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    const formValue = this.adapterForm.value;
    const request: Partial<CreateAdapterRequest> = {};

    // Only include name if it's not null
    if (formValue.name != null) {
      request.name = formValue.name;
    }

    // Only include description if it's not null and (for create mode always, for update mode only if not empty)
    if (formValue.description != null && (!this.isEditMode || formValue.description.trim() !== '')) {
      request.description = formValue.description;
    }

    // Get config from the appropriate child component
    if (this.selectedPlatform === AdapterPlatform.Splunk && this.splunkForm) {
      const config = this.splunkForm.getFormValue();
      if (Object.keys(config).length > 1) {
        request.config = config as AdapterConfig;
      }
    } else if (this.selectedPlatform === AdapterPlatform.MicrosoftSentinel && this.sentinelForm) {
      const config = this.sentinelForm.getFormValue();
      if (Object.keys(config).length > 1) {
        request.config = config as AdapterConfig;
      }
    }

    const apiCall =
      this.isEditMode && this.data.adapterId
        ? this._adaptersHttpService.updateAdapter$(this.data.adapterId, request)
        : this._adaptersHttpService.createAdapter$(request as CreateAdapterRequest);

    apiCall.subscribe({
      next: (adapter) => {
        this._logger.info(`Adapter ${this.isEditMode ? 'updated' : 'created'} successfully:`, adapter);
        this.isLoading = false;
        this._dialogRef.close(true);
      },
      error: (error) => {
        this._logger.error(`Failed to ${this.isEditMode ? 'update' : 'create'} adapter:`, error);
        this.errorMessage = this._parseBackendError(error);
        this.isLoading = false;
      },
    });
  }

  public get isFormValid(): boolean {
    // Check common form validity
    const commonFormValid = this.adapterForm.valid && this.selectedPlatform !== null && !this.isLoading;

    // Check child component form validity
    let childFormValid = true;
    if (this.selectedPlatform === AdapterPlatform.Splunk && this.splunkForm) {
      childFormValid = this.splunkForm.isValid();
    } else if (this.selectedPlatform === AdapterPlatform.MicrosoftSentinel && this.sentinelForm) {
      childFormValid = this.sentinelForm.isValid();
    }

    return commonFormValid && childFormValid;
  }
}
